// src/services/fcmService.ts
import { getToken, onMessage, Messaging } from 'firebase/messaging';
import { deleteDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { messaging, db } from './firebase';

const isLocalDevelopmentHost =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname);

class FCMService {
  private messaging: Messaging | null = null;

  constructor() {
    this.messaging = messaging;
  }

  // طلب إذن الإشعارات
  async requestPermission(): Promise<NotificationPermission> {
    try {
      if (!('Notification' in window)) {
        console.warn('This browser does not support notifications');
        return 'denied';
      }

      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  // التحقق من حالة الإذن
  checkPermission(): NotificationPermission {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }

  // تسجيل Service Worker
  async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.register(
          '/firebase-messaging-sw.js',
          { scope: '/' }
        );
        await navigator.serviceWorker.ready;

        return registration;
      }
      console.warn('Service Worker not supported');
      return null;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }

  // الحصول على FCM Token
  async getToken(userId: string): Promise<string | null> {
    try {
      if (isLocalDevelopmentHost) {
        console.info('FCM device token registration is skipped on localhost.');
        return null;
      }

      if (!this.messaging) {
        console.warn('Messaging not initialized');
        return null;
      }

      // التحقق من الإذن
      const permission = this.checkPermission();
      if (permission !== 'granted') {
        const newPermission = await this.requestPermission();
        if (newPermission !== 'granted') {
          console.warn('Notification permission denied');
          return null;
        }
      }

      // تسجيل Service Worker والانتظار حتى يصبح نشطاً
      const registration = await this.registerServiceWorker();
      if (!registration) {
        console.error('Service Worker registration failed');
        return null;
      }

      // الحصول على Token
      const vapidKey =
        process.env.REACT_APP_FIREBASE_VAPID_KEY ||
        'BKvTaf8tztC4dIyUoZ4cZubWcPeO868WX37tAYiUYw9BBWtGfsGkDsSXIIgDne_pyxjImbli_wCFB17wTe6WYUY';

      const token = await getToken(this.messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        await this.saveFCMToken(userId, token);
        return token;
      } else {
        console.warn('No registration token available');
        return null;
      }
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  }

  // حفظ FCM Token في Firestore
  async saveFCMToken(userId: string, token: string): Promise<void> {
    try {
      const tokenId = await this.hashToken(token);
      await setDoc(
        doc(db, 'users', userId, 'fcmTokens', tokenId),
        {
          token,
          active: true,
          platform: navigator.platform || 'web',
          userAgent: navigator.userAgent.slice(0, 500),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Error saving FCM token:', error);
    }
  }

  // معالجة الإشعارات في المقدمة (Foreground)
  onForegroundMessage(callback: (payload: any) => void): () => void {
    if (!this.messaging) {
      console.warn('Messaging not initialized');
      return () => undefined;
    }

    return onMessage(this.messaging, payload => {
      callback(payload);
    });
  }

  // إلغاء تسجيل Token (عند تسجيل الخروج)
  async removeToken(userId: string, token: string): Promise<void> {
    try {
      const tokenId = await this.hashToken(token);
      await deleteDoc(doc(db, 'users', userId, 'fcmTokens', tokenId));
    } catch (error) {
      console.error('Error removing FCM token:', error);
    }
  }

  private async hashToken(token: string): Promise<string> {
    const bytes = new TextEncoder().encode(token);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map(value => value.toString(16).padStart(2, '0'))
      .join('');
  }

  // تهيئة FCM للمستخدم
  async initializeFCM(
    userId: string,
    onMessageCallback?: (payload: any) => void
  ): Promise<void> {
    try {
      // الحصول على Token
      const token = await this.getToken(userId);

      if (token) {
        // معالجة الإشعارات في المقدمة
        if (onMessageCallback) {
          this.onForegroundMessage(onMessageCallback);
        }
      }
    } catch (error) {
      console.error('Error initializing FCM:', error);
    }
  }
}

export const fcmService = new FCMService();
export default fcmService;
